<template>
  <div class="custom-page-container">
    <!-- Error State -->
    <div v-if="!loading && error" class="error-state">
      <h1>Page Not Found</h1>
      <p>{{ error }}</p>
      <router-link to="/">Return to Home</router-link>
    </div>

    <!-- Page Content -->
    <!-- While the next page loads, the previous one keeps its height but is hidden -->
    <div v-else-if="page" :key="loadedSlug" class="custom-page" :class="{ 'is-switching': loadedSlug !== effectiveSlug }">
      <div v-if="sortedBlocks.length" class="page-blocks">
        <BlockRenderer v-for="block in sortedBlocks" :key="block.id" :block="block" :eager="eagerBlockIds.has(block.id)" />
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref } from 'vue'
import { useHead } from '@unhead/vue'
import { loadFirestore } from '@/utils/loadFirestore'
import { store, cacheUtils } from '@/store'
import { artistConfig as defaultConfig } from '@/config/artist.config'
import { getTenantUid } from '@/utils/tenantUtils'
import { mark, summary } from '@/utils/perfMarks'
import { restGetDocFields, restFieldsToPlain, restGetSiteBundle } from '@/utils/firestoreRest'
import BlockRenderer from '@/components/blocks/BlockRenderer.vue'

export default {
  name: 'CustomPage',
  components: {
    BlockRenderer
  },
  props: {
    slug: {
      type: String,
      default: null
    }
  },
  setup(props) {
    const config = computed(() => store.artistConfig || defaultConfig)
    const isHome = computed(() => props.slug === '_home')
    const pageSeo = ref({})
    const pageName = ref('')
    // No SEO title: Home uses the Site title, other pages "{page name} | {Site title}"
    const pageTitle = computed(() => {
      const siteTitle = config.value.site.defaultTitle
      if (pageSeo.value?.title) return pageSeo.value.title
      if (isHome.value || !pageName.value) return siteTitle
      return siteTitle ? `${pageName.value} | ${siteTitle}` : pageName.value
    })

    useHead({
      title: pageTitle,
      meta: computed(() => {
        const seo = pageSeo.value || {}
        const description = seo.description || config.value.site.defaultDescription

        return [{ name: 'description', content: description }]
      })
    })

    return { pageSeo, pageName }
  },
  data() {
    return {
      page: null,
      loadedSlug: null, // slug the current `page` belongs to
      loading: true,
      error: null
    }
  },
  computed: {
    effectiveSlug() {
      return this.slug || this.$route.params.slug
    },
    sortedBlocks() {
      if (!this.page) return []

      return [...(this.page.blocks || [])].sort((a, b) => a.order - b.order)
    },
    // The first two image-bearing blocks load eagerly (likely above the fold). A gallery
    // counts as image-bearing (it occupies a slot) but prioritizes its own first rows,
    // so it never receives `eager` itself.
    eagerBlockIds() {
      const hasFilledImageSlot = (block) => {
        const slots = block.data?.slots || []
        return slots.some(slot => slot?.type === 'image' && slot.data?.imageUrl)
      }
      const bearsImages = (block) => {
        if (block.type === 'image' || block.type === 'gallery') return true
        return block.type === 'columns' && hasFilledImageSlot(block)
      }
      const firstTwoImageBearing = this.sortedBlocks.filter(bearsImages).slice(0, 2)
      const eligible = firstTwoImageBearing.filter(block => block.type !== 'gallery')
      return new Set(eligible.map(block => block.id))
    },
    contentShown() {
      return !!this.page || (!this.loading && !!this.error)
    }
  },
  watch: {
    effectiveSlug() {
      this.loadPage()
    },
    contentShown(shown) {
      store.pageContentShown = shown
    }
  },
  async mounted() {
    store.pageContentShown = this.contentShown
    await this.loadPage()
  },
  unmounted() {
    store.pageContentShown = false
  },
  methods: {
    async loadPage() {
      mark('page:load:start')
      this.error = null

      const slug = this.effectiveSlug
      const tenantUid = getTenantUid()
      if (!tenantUid) {
        this.error = 'Tenant not resolved.'
        this.loading = false
        mark('page:load:end')
        return
      }

      // Render from the in-memory entry only if it's a FULL page (has blocks). Visitor-boot
      // entries are nav-only projections → fall through to the cache-first + REST read.
      const fromStore = store.customPages.find(p => p.id === slug)
      if (fromStore && fromStore.blocks && (slug === '_home' || !fromStore.isHidden)) {
        this.page = fromStore
        this.loadedSlug = slug
        this.pageSeo = fromStore.seo || {}
        this.pageName = fromStore.title || ''
        this.loading = false
        mark('page:load:end')
        if (slug === '_home') summary('visitor home load')
        return
      }

      // Cache-first paint: render instantly from localStorage while we revalidate
      // against Firestore in the background.
      let markedEnd = false
      const cached = cacheUtils.loadPageFromCache(tenantUid, slug)
      if (cached && !(slug !== '_home' && cached.isHidden)) {
        this.page = cached
        this.loadedSlug = slug
        this.pageSeo = cached.seo || {}
        this.pageName = cached.title || ''
        this.loading = false
        mark('page:load:end')
        if (slug === '_home') summary('visitor home load')
        markedEnd = true
      } else {
        this.loading = true
      }

      try {
        // Home prefers the edge bundle; other pages and a bundle miss read direct (REST first
        // to skip the SDK's cold handshake ~700ms, SDK fallback, 404 = missing).
        let pageData = null
        if (slug === '_home') {
          const bundle = await restGetSiteBundle()
          if (bundle?.home) pageData = bundle.home
        }
        if (!pageData) {
          try {
            const fields = await restGetDocFields(`users/${tenantUid}/customPages/${slug}`)
            pageData = fields ? restFieldsToPlain(fields) : null
          } catch {
            const { db, doc, getDoc } = await loadFirestore()
            const pageSnapshot = await getDoc(doc(db, 'users', tenantUid, 'customPages', slug))
            pageData = pageSnapshot.exists() ? pageSnapshot.data() : null
          }
        }

        if (this.effectiveSlug !== slug) return

        if (!pageData) {
          this.page = null
          this.error = slug === '_home'
            ? 'No content has been added to the home page yet.'
            : `Page "${slug}" not found.`
          return
        }

        if (slug !== '_home' && pageData.isHidden) {
          this.page = null
          this.error = 'This page is not available.'
          cacheUtils.savePageToCache(tenantUid, slug, { isHidden: true })
          return
        }

        const page = { id: slug, ...pageData }
        this.page = page
        this.loadedSlug = slug
        this.pageSeo = pageData.seo || {}
        this.pageName = pageData.title || ''
        cacheUtils.savePageToCache(tenantUid, slug, page)
      } catch (error) {
        console.error('Error loading page:', error)
        if (!this.page || this.loadedSlug !== slug) {
          this.page = null
          this.error = 'Failed to load page. Please try again later.'
        }
      } finally {
        this.loading = false
        if (!markedEnd) {
          mark('page:load:end')
          if (this.effectiveSlug === '_home') summary('visitor home load')
        }
      }
    }
  }
}
</script>

<style scoped>

.custom-page-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
  min-height: 100%;
}

.error-state {
  text-align: center;
  padding: 80px 20px;

  h1 {
    font-size: 32px;
    color: var(--color-text);
    margin-bottom: 15px;
  }

  p {
    font-size: var(--font-sm);
    color: color-mix(in srgb, var(--color-text) 75%, var(--color-bg));
    margin-bottom: 25px;
  }

  a {
    display: inline-block;
    padding: 12px 24px;
    background-color: var(--color-green);
    color: var(--color-accent-text);
    text-decoration: none;
    border-radius: var(--radius);
    transition: background-color var(--transition-base);

    &:hover {
      background-color: var(--color-green-hover);
    }
  }
}

.custom-page {
  animation: fadeIn var(--transition-base);

  &.is-switching {
    visibility: hidden;
  }
}

.page-title {
  font-size: 42px;
  font-weight: bold;
  color: var(--color-text);
  margin-bottom: 40px;
  text-align: center;
  border-bottom: 2px solid var(--color-lightest-gray);
  padding-bottom: 20px;
}

.empty-page {
  text-align: center;
  padding: 60px 20px;
  color: var(--color-dark-gray);
  font-style: italic;
}

.page-blocks {
  margin-top: 0;
}

@keyframes fadeIn {
  from { opacity: 0.6; } /* fade start on custom page */
  to { opacity: 1; }
}

@media only screen and (max-width: 768px) {
  .page-title {
    font-size: 32px;
    margin-bottom: 30px;
  }
}
</style>
